import React, { useState, useRef, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import {fontQuicksand} from './mixin';
import Styled from 'styled-components';
import { device } from '../../utils/responsive_utilities';
import Config from '../../../js_bytesize/config/purchase/config';
import PaymentCta from './purchase-cta';
import Image from '../loggedout/common/Image';

import Visa from '~images/mela-purchase/visa.svg';
import Amex from '~images/mela-purchase/amex.svg';
import MasterCard from '~images/mela-purchase/mastercard.svg';
import Discover from '~images/mela-purchase/discover.svg';
import { deleteItemFromLocalStorage, getItemFromLocalStorage, REGEX_COLLECTION } from '../../utils/web-helper';
import GlobalSpinner from '../../utils/global-spinner';

const CARD_ELEMENT_STYLES = {
  style: {
    base: {
      color: '#fff',
      fontFamily: 'QuickSand, Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '2px',
      '::placeholder': {
        color: '#313575'
      },
      ':-webkit-autofill': {
        backgroundColor: 'transparent'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const CheckoutForm = ({settings, paymentHandler, selectedCardId, error, setError, skipSignUp, paymentSuccessHandlerForSVTrial,ptlParent,parentDobYear,restrictionInfoStore, showDiscountChanges, selectedPlan}) => {
  // const [error, setError] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const [postalCode, setPostalCode] = useState(null);
  const [eventLogged, setEventLogged] = useState(false);
  const [email, setEmail] = useState(null);
  const isEmailRequried = (skipSignUp || restrictionInfoStore) && !settings.currentUser;
  const errorElement = useRef(null);
  let { showZipOnPurchase, trailDays, trialAvailable } = settings;
  const [disableBtn, setDisableBtn] = useState(false);

  // Handle real-time validation errors from the card Element.
  const eventLogger = () => {
    if(!eventLogged) {
      setEventLogged(true);
      trackAnalyticsEvent('Interacted Product Purchase Form');
    }
  }
  const handleChange = (event) => {
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
    trackAnalyticsEvent("Interacted Plan Purchase Combined Page",{"Interaction Details":"Started Typing Credit Card Details"})
  }
  useEffect(() => {
    if(error) {
      setDisableBtn(false)
    }
  }, [error])

  useEffect(() => {
    if(showZipOnPurchase) {
      trackAnalyticsEvent("ZipCode visible", {"Show Zip On Purchase": showZipOnPurchase, "Country Code": settings.country_code });
    }
  }, [showZipOnPurchase])

  // Handle form submission.
  const handleSubmit = async (event) => {
    if(disableBtn) return null;
    !error && setDisableBtn(true);
    event.preventDefault();
    trackAnalyticsEvent("Interacted Plan Purchase Combined Page",{
      "Clicked On": "Start Trial"
    });
    trackAnalyticsEvent("Credit Card Submitted");
    errorElement && errorElement.current ? errorElement.current.scrollIntoView({ behavior: "smooth" }) : null;
    if (selectedCardId) {
      trackAnalyticsEvent("Credit Card Submitted Correctly");
      paymentHandler(selectedCardId)
    } else {
      const card = elements.getElement(CardNumberElement);
      try {
        const result = await stripe.createToken(card);
        if (result.error) {
          trackAnalyticsEvent('Payment Form Error',
          {'Error type': 'Wrong Card Details', 'Error code': result.error.code});
          // Inform the user if there was an error.
          setError(result.error.message);
        } else {
          setError(null);
          if (showZipOnPurchase) {
            const isZipCodeValid = isValidUSAandCAZipCode(postalCode)
            const eventName = isZipCodeValid ? "ZipCode validation success" : "ZipCode validation failed"
            trackAnalyticsEvent(eventName , {
              "On Click": "Submit Button",
              "Show Zip On Purchase": showZipOnPurchase, 
              "Country Code": settings.country_code, 
              "Postal Code Entered": postalCode
            });
            if (!isZipCodeValid) {
              return;
            }
          }
          // Send the token to your server.
          if (isEmailRequried) {
            if (!email) {
              trackAnalyticsEvent('Payment Form Error', {'Error type': 'Empty Email Field'});
              return setError('Please enter your email');
            }
            if (!REGEX_COLLECTION.EMAIL_REGEX.test(email)) {
              trackAnalyticsEvent('Payment Form Error', {'Error type': 'Wrong Email ID'});
              return setError('Please enter valid email');
            }
          }
          if(restrictionInfoStore){
            // parent creation in case where user cancels the 3pm popup
            const response = await parentCreation()
            response['stripe_token'] = result.token.id
            response['emailId'] = email
            saveAndSubscribe(response);
          }else {
            result['stripe_token'] = result.token.id
            saveAndSubscribe(result)
          }
        }
      } catch(e) {
        console.error(e);
        setError('Something went wrong, please try again');
      }
    }
  };

  const zipChangeHandler = (e) => {
    let zipCode = e ? e.target.value : '';
    setPostalCode(zipCode);
    isValidUSAandCAZipCode(zipCode);
  }

  const isValidUSAandCAZipCode = (zipCode) => {
    let validZipCode = false;
    let canadaRegex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
    let usRegex = /^\d{5}(-\d{4})?$/;
    if (zipCode && (settings?.country_code == "US" ? usRegex : canadaRegex).test(zipCode)){
      setError(null);
      eventLogger();
      validZipCode = true;
    } else {
      setError('Invalid Zip Code');
    }
    return validZipCode;
  };

  const emailHandler = (e) => {
    setEmail(e.target.value);
  }

  const saveAndSubscribe = async (data) => {
    // this.setState({isSubmitDisabled: true});
    let params = {...data} || {};

    trackAnalyticsEvent("Credit Card Submitted Correctly");
    try{
      const response = await saveCard(params)
      let subscribeData = {...data} || {};
      subscribeData["stripe_card_id"] = response.stripe_card.stripe_id
      // delete subscribeData.stripe_token
      const apiParams = {cardAdded: true};
      paymentHandler(subscribeData, apiParams);
      // this.props.submitCallback && this.props.submitCallback(subscribeData, {cardAdded: true})
    }
     catch(err){
        const error = (err && err.message || err.error) || 'Error from backend';
        trackAnalyticsEvent('Payment Page Error Viewed',{
          "Error Message": error,
          "Error Type": "Email Exists"
        });
        setError(error);
      }
  }

  // Save Card
  const saveCard = async (newData) => {
    if(!Stripe){
      return Promise.reject()
    }
    if (showZipOnPurchase) {
      newData['postal_code'] = postalCode;
      trackAnalyticsEvent("Postal Code In Save Credit Card API", {"Show Zip On Purchase": showZipOnPurchase, "Country Code": settings.country_code, "Postal Code": postalCode });
    }

    if (paymentSuccessHandlerForSVTrial) {
      try {
        await paymentSuccessHandlerForSVTrial(!settings.currentUser ? email : null, true);
      } catch(err) {
        const errMsg = err && err.errors && err.errors[0] || err.msg || `Something went wrong, please try again`;
        console.error(err);
        setError(errMsg)
        return;
      }
    }

    return new Promise((resolve, reject) => {
      Config.DataApi.saveCreditCard(newData, function(data){
        if(data.stripe_card){
          resolve({stripe_card: data.stripe_card})
        }else{
          setError('Something went wrong, please try again');
          reject(data);
        }
      }, paymentSuccessHandlerForSVTrial ? true : false);
    });
  }

  const parentCreation = () => {
    let options = {
      user: {
        "email": email,
        "type": 'parent',
        "dob_year": parentDobYear || getItemFromLocalStorage('parentDobYear'),
        "time_zone": Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }
    if(ptlParent) {
      return new Promise((resolve, reject) => {
        resolve( {
          "via_remote_login" : true,
          "parent_id": ptlParent.id
        })
      })
    }
    return new Promise((resolve, reject) => {
      Config.DataApi.createParentAndAddChild(options, (data) =>{
        deleteItemFromLocalStorage('parentDobYear');
        if(data.success) {
          resolve( {
            "via_remote_login" : true,
            "parent_id": data.parent_id
          })
        }
        else {
          GlobalSpinner.hide();
          trackAnalyticsEvent('Payment Form Error', {'Error type': data.email_error});
          setError(data.message || 'Something went wrong, please try again');
          return;
        }
      })
    });
  }

  const getTrialEndDate = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let curr_date = new Date();
    let timeStampOfTrialEnd = curr_date.setDate(curr_date.getDate() + trailDays || 7);
    let trialEndDate = new Date(timeStampOfTrialEnd);
    return `${months[trialEndDate.getMonth()]} ${trialEndDate.getDate()}, ${trialEndDate.getFullYear()}`
  }

  return (
    <form onSubmit={handleSubmit}>
      {!selectedCardId && <>
        <StripeElementWrapper>
          <label htmlFor="card-number-element">CARD NUMBER</label>
          <CardBrandIcons />
          <CardNumberElement
            id="card-number-element"
            options={CARD_ELEMENT_STYLES}
            onChange={handleChange}
            className="card-inp-element"
          />
        </StripeElementWrapper>
        <DateCvcElementWrap>
          <StripeElementWrapper marginRight={true}>
            <label htmlFor="card-expiry-element">EXPIRY DATE</label>
            <CardExpiryElement
              id="card-expiry-element"
              options={CARD_ELEMENT_STYLES}
              onChange={handleChange}
              className="card-inp-element"
            />
          </StripeElementWrapper>

          <StripeElementWrapper>
            <label htmlFor="card-cvc-element">CVC</label>
            <CardCvcElement
              id="card-cvc-element"
              options={CARD_ELEMENT_STYLES}
              onChange={handleChange}
              className="card-inp-element"
            />
          </StripeElementWrapper>
        </DateCvcElementWrap>
        {showZipOnPurchase ? <StripeElementWrapper>
            <label htmlFor="zipcode-element">BILLING ZIP CODE</label>
            <ZipCodeElement
              type="text"
              id="zipcode-element"
              className="card-inp-element"
              placeholder="Billing Zip Code"
              value={postalCode}
              maxLength={10}
              onChange={(e) => zipChangeHandler(e)}
            />
          </StripeElementWrapper> : null}

        {isEmailRequried ?
          <StripeElementWrapper>
            <label htmlFor="email-element">EMAIL</label>
            <ZipCodeElement
              type="text"
              id="email-element"
              className="email-inp-element"
              placeholder=""
              value={email}
              onChange={(e) => emailHandler(e)}
            />
          </StripeElementWrapper> : null
        }
          </> }
        <CardError className="card-errors" role="alert" ref={errorElement}>{error}</CardError>

      { trialAvailable &&
        <TrialDate>You will not be charged until your FREE trial ends on {getTrialEndDate()}</TrialDate>
      }
      <PaymentCta clickHandler={handleSubmit} fromCreditForm={true} settings={settings} disableBtn={disableBtn} showDiscountChanges={showDiscountChanges} selectedPlan={selectedPlan}/>
      <SecureText zipCodeVisible={showZipOnPurchase}>Secure payment. We use 256-bit encryption. The same that banks use</SecureText>
     <SecureText>*Taxes will be applicable as per the local tax laws.</SecureText>
    </form>
  );
}

const CardBrandIcons = () => {
  return (
    <CardBrandsIconWrap>
      <Image src={Visa} />
      <Image src={Amex} />
      <Image src={Discover} />
      <Image src={MasterCard} />
    </CardBrandsIconWrap>
  )
}

const CreditCardForm = ({settings, paymentHandler, selectedCardId, skipSignUp, error, setError, paymentSuccessHandlerForSVTrial, isMentorAlloted, ptlParent, parentDobYear, restrictionInfoStore,selectedPlan,showDiscountChanges}) => {
  const stripePromise = loadStripe(settings.stripeKey);
  return (
    <Elements stripe={stripePromise} >
      <CheckoutForm
        settings = {settings}
        paymentHandler = {paymentHandler}
        selectedCardId = {selectedCardId}
        error = {error}
        setError = {setError}
        skipSignUp = {skipSignUp}
        paymentSuccessHandlerForSVTrial = {paymentSuccessHandlerForSVTrial}
        ptlParent={ptlParent}
        parentDobYear={parentDobYear}
        restrictionInfoStore={restrictionInfoStore}
        showDiscountChanges={showDiscountChanges} 
        selectedPlan={selectedPlan}
      />
    </Elements>
  );
}
export default CreditCardForm;

export const StripeElementWrapper = Styled.div`
  text-align: left;
  padding: 0.8rem 0;
  flex: 1;
  ${props=>props.marginRight && `margin-right:3.3rem;`};
  position: relative;
  label {
    color: #9294F6;
    ${fontQuicksand(1.2, 1.6, 7)}
    letter-spacing: 2px;
  }
  .card-inp-element {
    padding: 1rem 0 0;
    border-bottom: 1px solid #4F52C3;
  }
`;

export const DateCvcElementWrap = Styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ZipCodeElement = Styled.input`
  background: transparent;
  border: 0;
  border-bottom: 1px solid #4F52C3;
  display: block;
  padding: 1rem 0 0;
  width: 100%;
  ${fontQuicksand(1.6, 2.0, 7)}
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  ::placeholder {
    color: #313575;
  }
`;

export const CardError = Styled.p`
  color: #EB6D45;
  ${fontQuicksand(1.2, 1.6, 5)};
  min-height: 1.7rem;
`;

export const CardBrandsIconWrap = Styled.div`
  position: absolute;
  right: 0;
  bottom: 1.4rem;
  img {
    margin-right: 0.5rem;
  }
`;


export const SecureText = Styled.p`
  color: #9294F6;
  margin: 0.2rem 0;
  ${fontQuicksand(1.2, 1.6, 5)}
  @media ${device.PhoneLargeScreen} {
    ${fontQuicksand(1, 1.4, 5)}
  }
  @media ${device.Phone} {
    ${fontQuicksand(1, 1.4, 5)};
    margin-bottom: 1rem;
  }
  @media ${device.Tablet} and (orientation: portrait) {
    margin-bottom: 1rem;
  }
`;

export const TrialDate = Styled.p`
  ${fontQuicksand(1.4, 1.8, 5)}
  margin: 1rem 0;
  @media ${device.PhoneLargeScreen} {
    ${fontQuicksand(1.1, 1.5, 5)}
  }
  @media ${device.Phone} {
    ${fontQuicksand(1.1, 1.5, 5)}
  }
`;